import { memo, useCallback, useState } from "react";

import PlusIcon from "@/common/images/icons/plus.svg?react";
import MoreIcon from "@/common/images/icons/moreIcon.svg?react";
import Tag from "components/core/tags/Tag";

import { AddingTag } from "../AddingTag";
import { IconSecondaryButton } from "components/core/IconSecondaryButton";
import { RemovableTag } from "../RemovableTag";

import classes from "./TagManager.module.scss";
import { useUserStore } from "@/common/store";

interface IProps {
  tags: string[];
  onChange: (tags: string[]) => void;
  visibleTagLimit?: number;
  minTags?: number;
  maxTags?: number;
  onAddTag?: () => void;
  onRemoveTag?: () => void;
}

export const TagManager = memo((props: IProps) => {
  const {
    tags,
    visibleTagLimit,
    minTags,
    maxTags,
    onChange ,
    onAddTag,
    onRemoveTag
  } = props;

  const verified = useUserStore((state) => state.currentUser?.verified);

  const [isShowMore, setShowMore] = useState(false);
  const [isAddingMode, setAddingMode] = useState(false);

  const handleShowMore = () => {
    setShowMore(prevState => !prevState);
  };

  const handleEnableAddingMode = useCallback(() => {
    setAddingMode(true);
  }, [verified]);

  const handleDisableAddingMode = useCallback(() => {
    setAddingMode(false);
  }, []);

  const handleRemoveTag = useCallback((tag: string) => () => {
    if (minTags && tags.length <= minTags) {
      return;
    }

    const index = tags.indexOf(tag);

    if (index === -1) {
      return;
    }

    const newTags = [...tags];
    newTags.splice(index, 1);
    onChange(newTags);
    onRemoveTag && onRemoveTag();
  }, [onChange, tags, verified]);

  const handleAddedTag = useCallback((tag: string) => () => {
    if (maxTags && tags.length >= maxTags) {
      return;
    }

    const newTags = [...tags];
    newTags.push(tag);
    onChange(newTags);
    onAddTag && onAddTag();
    handleDisableAddingMode();
  }, [onChange, tags, handleDisableAddingMode]);

  return (
    <div className={classes.tagManager}>
      {
        !maxTags || tags.length < maxTags ? (
          isAddingMode ? (
            <AddingTag addedTags={tags} onAdded={handleAddedTag} onClose={handleDisableAddingMode}/>
          ) : (
            <IconSecondaryButton icon={<PlusIcon />} variant="active" onClick={handleEnableAddingMode} aria-label="add tag"/>
          )
        ) : null
      }

      {
        tags.map((tag, index) => {
          if (!visibleTagLimit || isShowMore || (!isShowMore && index < visibleTagLimit)) {
            return (
              !minTags || tags.length > minTags ? (
                <RemovableTag key={tag} tag={tag} onRemove={handleRemoveTag}/>
              ) : (
                <Tag key={tag} tag={tag}/>
              )
            );
          } else {
            return null;
          }
        })
      }

      {
        visibleTagLimit && tags.length > visibleTagLimit ? (
          <IconSecondaryButton icon={<MoreIcon />} onClick={handleShowMore} aria-label="show more"/>
        ) : null
      }

    </div>
  );
});
